<template>

    <div>

        <!-- ACTION BUTTONS -->
        <div class="d-flex justify-end pr-3">

            <v-tooltip top color="primary">
                <template v-slot:activator="{ on }">
                    <v-btn class="mr-2 elevation-0 align-self-center"
                           :disabled="!hasPermission('view users')"
                           @click="getRoles(true)"
                           v-on="on"
                           icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>Reload data</span>
            </v-tooltip>

            <v-tooltip top color="primary">
                <template v-slot:activator="{ on }">
                    <v-btn v-if="hasPermission('create roles')"
                           v-on="on"
                           fab
                           small
                           class="elevation-0 mr-2"
                           color="primary"
                           @click="roleDialogModel=!roleDialogModel">
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </template>
                <span>Create new role</span>
            </v-tooltip>
        </div>

        <!-- ROLES TABLE -->
        <v-data-table v-if="hasPermission('view roles')"
                      v-bind:headers="roleHeaders"
                      :items="orderedRoles"
                      class="elevation-1"
                      :loading="rolesLoading || deleting"
                      :options.sync="tableOptions"
                      :footer-props="{itemsPerPageOptions: [10,50, -1]}"
        >
            <template v-slot:body="{ items }">
                <tbody>
                <tr v-for="item in items" :key="item.id">

                    <td class="text-left">
                        <v-icon>mdi-security</v-icon>
                    </td>

                    <!-- ROLE NAME -->
                    <td class="text-left text-no-wrap">
                        {{ item.name }}
                    </td>

                    <!-- WARNINGS -->
                    <td class="text-center">
                        <dangerous-permissions-icon :permissions="item.permissions"/>
                    </td>

                    <!-- VIEW PERMISSIONS -->
                    <td class="text-center">
                        <v-tooltip color="primary" top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    class="ma-0 elevation-0"
                                    small
                                    color="primary"
                                    v-on="on"
                                    @click="openPermissionDialog(item.permissions)"
                                    :disabled="item.permissions.length===0 || !hasPermission('view roles')">
                                    <v-icon left light small color="white">mdi-eye</v-icon>
                                    View
                                </v-btn>
                            </template>
                            View Permissions
                        </v-tooltip>
                    </td>

                    <td class="text-left">
                        <div class="text-no-wrap">

                            <v-tooltip top color="primary" open-delay="500">
                                <template v-slot:activator="{ on }">
                                    <v-btn :disabled="!hasPermission('update roles') || item.default === true"
                                           class="elevation-0 mr-2"
                                           icon
                                           x-small
                                           v-on="on"
                                           @click.stop="editRoleClicked(item)">
                                        <v-icon light small color="white">mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>Edit Role</span>
                            </v-tooltip>

                            <v-tooltip top color="primary" open-delay="500">
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        :disabled="!hasPermission('delete roles') || item.default === true || hasRole(item.name)"
                                        class="elevation-0"
                                        icon
                                        x-small
                                        v-on="on"
                                        @click.stop="deleteRoleClicked(item)">
                                        <v-icon light small color="white">mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>Delete Role</span>
                            </v-tooltip>
                        </div>
                    </td>

                </tr>
                </tbody>
            </template>
        </v-data-table>

        <edit-role-dialog
            v-if="hasPermission('create roles') || hasPermission('update roles')"
            v-model="roleDialogModel"
            :permissions="permissions"
            :role="roleUnderEdit"
        ></edit-role-dialog>

        <view-permissions-dialog v-model="viewPermissionsDialogModel"
                                 :items="selectedRolePermissions"
        ></view-permissions-dialog>

        <delete-role-confirmation-dialog v-if="roleUnderDelete"
                                         v-model="showRoleConfirmationDialog"
                                         delete
                                         :role="roleUnderDelete"
                                         @data="doRoleDelete"
        ></delete-role-confirmation-dialog>

    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import {mapState, mapActions} from 'vuex';
import EditRoleDialog from "./EditRoleDialog";
import DeleteRoleConfirmationDialog from "./DeleteRoleConfirmationDialog";
import ViewPermissionsDialog from "../ViewPermissionsDialog";
import {dangerousPermissionsMixin} from "../../../../components/mixins/dangerousPermissionsMixin";
import DangerousPermissionsIcon from "../DangerousPermissionsIcon";

const vuexStore = 'UserManager';

export default {

    mixins: [dangerousPermissionsMixin],

    components: {
        DangerousPermissionsIcon,
        ViewPermissionsDialog,
        EditRoleDialog,
        DeleteRoleConfirmationDialog,
        Json
    },

    computed: {

        ...mapState(vuexStore, [
            'roles',
            'rolesLoading',
            'permissions'
        ]),

        orderedRoles() {
            return this.roles.sort((a, b) => a.default !== b.default ? 0 : a.default ? -1 : 1);
        },
    },

    data() {
        return {
            showRoleEditorDialog: false,
            showRoleConfirmationDialog: false,

            selectedRolePermissions: null,
            viewPermissionsDialogModel: false,

            roleDialogModel: false,
            roleUnderEdit: null,
            roleUnderDelete: null,
            deleting: false,

            tableOptions: {
                sortBy: ['name'],
                sortDesc: [false],
            },

            roleHeaders: [
                {
                    align: 'left',
                    text: '',
                    sortable: false,
                    value: 'name',
                    width: '1px'
                },
                {
                    align: 'left',
                    text: 'NAME',
                    sortable: true,
                    value: 'name',
                },
                {
                    text: 'VIEW PERMISSION',
                    align: 'center',
                    sortable: false,
                    value: 'name',
                },
                {
                    text: '# PERMISSIONS',
                    align: 'center',
                    sortable: false,
                    value: 'name',
                },
                {
                    text: 'ACTIONS',
                    align: 'left',
                    sortable: false,
                    value: 'name',
                    width: '100px'
                },
            ],
        }
    },

    methods: {

        ...mapActions('UserManager', [
            'getRoles',
            'deleteRole',
            'saveRole'
        ]),

        deleteRoleClicked(item) {
            this.roleUnderDelete = item;
            this.showRoleConfirmationDialog = true;
        },

        editRoleClicked(role) {
            this.roleUnderEdit = Object.assign({}, role);
            this.roleDialogModel = true;
        },

        openPermissionDialog(permissions) {
            this.selectedRolePermissions = permissions;
            this.viewPermissionsDialogModel = true;
        },

        doRoleDelete(data) {
            if (data.confirmed) {

                this.deleting = true;

                this.deleteRole({
                    id: this.roleUnderDelete.id,
                })
                    .then(() => {
                        Bus.$emit('flash-message', {type: 'info', 'message': 'Role successfully deleted'});
                    })
                    .catch((error) => {
                        console.log(error);
                        Bus.$emit('flash-message', {type: 'error', 'message': error.response.data.message});
                    })
                    .finally(() => {
                        this.deleting = false;
                    })
            }
        },
    }
}
</script>
